// Cliente HTTP para el backend Laravel (migración desde los endpoints .php).
//
// Uso incremental: cada componente que se migre cambia su `fetch(`${baseURL}/algo.php`)`
// por `apiFetch('/ruta')`. NO cambia el `url.js` viejo, así conviven ambos backends
// durante la transición.

const getApiBase = () => {
  const host = window.location.hostname;
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return 'http://127.0.0.1:8000';        // Laravel en dev (php artisan serve --port=8000)
  }
  return window.location.origin;           // prod: mismo dominio que el front
};

export const apiBase = getApiBase();

const TOKEN_KEY = 'ceseco_token';
const REFRESH_KEY = 'ceseco_refresh';
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const setTokens = (access, refresh) => {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

// Rutas de auth que NUNCA deben disparar un refresh (evita recursión).
const AUTH_PATHS = ['/login', '/refresh', '/logout'];

// Un solo refresh compartido entre 401s concurrentes (evita la estampida). Los 401s
// *escalonados* se cubren aparte: cada request compara su token contra el actual y, si ya
// cambió, reintenta sin pedir otro refresh.
// ponytail: queda el caso multi-pestaña — dos pestañas pueden refrescar a la vez y una
// perder la rotación (re-login); el arreglo sería coordinar vía BroadcastChannel.
let refreshPromise = null;

/** Canjea el refresh token por un access nuevo. Devuelve el access nuevo o null si ya no se puede. */
async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  if (!refreshPromise) {
    refreshPromise = fetch(`${apiBase}/api/refresh`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${refresh}` },
    })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        if (data?.token) { setTokens(data.token, data.refresh_token); return data.token; }
        return null;
      })
      .catch(() => null)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

/** Sesión no recuperable (refresh expirado/ inválido): limpia tokens y vuelve al login. */
function forceLogout() {
  clearToken();
  window.location.reload();   // el guard de ruta muestra el login (mismo patrón que logout())
}

const shouldTryRefresh = (path) => getRefreshToken() && !AUTH_PATHS.includes(path);

/**
 * Llama a la API Laravel. Agrega el Bearer token automáticamente.
 * Si el access token expiró (401), refresca de forma transparente y reintenta una vez.
 * @param {string} path  ruta bajo /api, p.ej. '/pacientes'
 * @param {{method?:string, body?:any, isForm?:boolean, headers?:object}} opts
 */
export async function apiFetch(path, { method = 'GET', body = null, isForm = false, headers = {} } = {}) {
  const build = (token) => {
    const opts = {
      method,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    };
    if (body != null) {
      if (isForm) {
        opts.body = body;                    // FormData (uploads) — el navegador pone el Content-Type
      } else {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      }
    }
    return opts;
  };

  const tokenUsado = getToken();
  let res = await fetch(`${apiBase}/api${path}`, build(tokenUsado));

  // 401 = Sanctum rechazó en el middleware (la acción NO se ejecutó) → refrescar y reintentar es seguro.
  if (res.status === 401 && shouldTryRefresh(path)) {
    let nuevo = getToken();
    // Si otra petición ya refrescó mientras esta viajaba, el token ya cambió: reintenta
    // con él SIN pedir otro refresh (evita refrescos duplicados por 401s escalonados).
    if (nuevo === tokenUsado) nuevo = await refreshAccessToken();
    if (nuevo) res = await fetch(`${apiBase}/api${path}`, build(nuevo));
    else forceLogout();
  }

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const err = new Error(data?.message || data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    if (res.status === 401) clearToken();
    throw err;
  }
  return data;
}

/**
 * Igual que `fetch` pero apuntando a `${apiBase}/api${path}` y agregando el Bearer token.
 * Devuelve un Response, así que el código existente (`.then(r => r.json())`, `if (response.ok)`,
 * body FormData/JSON, method…) sigue funcionando sin cambios. Refresca+reintenta ante un 401.
 */
export async function apiRaw(path, opts = {}) {
  const build = (token) => {
    const headers = { Accept: 'application/json', ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return { ...opts, headers };
  };

  const tokenUsado = getToken();
  let res = await fetch(`${apiBase}/api${path}`, build(tokenUsado));

  if (res.status === 401 && shouldTryRefresh(path)) {
    let nuevo = getToken();
    if (nuevo === tokenUsado) nuevo = await refreshAccessToken(); // ya refrescó otra petición → reutiliza
    if (nuevo) res = await fetch(`${apiBase}/api${path}`, build(nuevo));
    else forceLogout();
  }

  return res;
}

/** Login: guarda el token y devuelve el perfil (misma forma que el viejo userLogued.php). */
export async function login(accessCode, contrasena) {
  const res = await fetch(`${apiBase}/api/login`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: new URLSearchParams({ access_code: accessCode, contrasena }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data?.message || 'Credenciales incorrectas');
    err.status = res.status;
    throw err;
  }
  setTokens(data.token, data.refresh_token);
  return data.usuario;
}

/** Perfil del usuario autenticado (← userLogued.php). */
export const fetchUsuario = () => apiFetch('/me');

/** Cierra sesión en el backend y limpia el token local. */
export async function logout() {
  try { await apiFetch('/logout', { method: 'POST' }); } catch { /* noop */ }
  clearToken();
}
